import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Trash2, Award, Check, ChevronsUpDown, UserX } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toLocalIso } from "@/utils/status";
import { useOutcomes } from "@/context/OutcomesContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function toInput(iso) {
  if (!iso) return "";
  return iso.slice(0, 16);
}
function fromInput(v) {
  if (!v) return "";
  return v.length === 16 ? `${v}:00` : v;
}

export default function AppointmentDialog({
  open, onOpenChange, initial, users, types, clients, onSaved, calendar = "consulenti",
}) {
  const { user } = useAuth();
  const { statusesFor, metaFor, resultsFor, resultMetaFor, isCompletedStatus } = useOutcomes();
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    title: "",
    collaborator_id: "",
    client_id: "",
    appointment_type_id: "",
    start: "",
    end: "",
    status: "da_svolgere",
    result: null,
    description: "",
    post_notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial?.id) {
      setForm({
        title: initial.title || "",
        collaborator_id: initial.collaborator_id || "",
        client_id: initial.client_id || "",
        appointment_type_id: initial.appointment_type_id || "",
        start: toInput(initial.start),
        end: toInput(initial.end),
        status: initial.status || "da_svolgere",
        result: initial.result || null,
        description: initial.description || "",
        post_notes: initial.post_notes || "",
      });
    } else {
      const start = initial?.start ? new Date(initial.start) : new Date();
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      setForm({
        title: "",
        collaborator_id: initial?.collaborator_id || user?.id || "",
        client_id: "",
        appointment_type_id: "",
        start: toLocalIso(start).slice(0, 16),
        end: toLocalIso(end).slice(0, 16),
        status: "da_svolgere",
        result: null,
        description: "",
        post_notes: "",
      });
    }
  }, [open, initial, user]);

  const onTypeChange = (typeId) => {
    const t = types.find((x) => x.id === typeId);
    if (t && form.start) {
      const startDt = new Date(form.start);
      const endDt = new Date(startDt.getTime() + (t.duration_min || 60) * 60 * 1000);
      setForm((f) => ({ ...f, appointment_type_id: typeId, end: toLocalIso(endDt).slice(0, 16) }));
    } else {
      setForm((f) => ({ ...f, appointment_type_id: typeId }));
    }
  };

  const onStatusChange = (newStatus) => {
    // Auto-clear result if new status is not completed
    setForm((f) => ({
      ...f,
      status: newStatus,
      result: isCompletedStatus(newStatus) ? f.result : null,
    }));
  };

  const statusList = statusesFor();
  const resultList = resultsFor(calendar);
  const statusIsCompleted = isCompletedStatus(form.status);

  const save = async () => {
    if (!form.collaborator_id || !form.start || !form.end) {
      toast.error("Compila consulente, inizio e fine.");
      return;
    }
    if (!form.title && !form.client_id) {
      toast.error("Inserisci un titolo oppure seleziona un cliente.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        start: fromInput(form.start),
        end: fromInput(form.end),
        client_id: form.client_id || null,
        appointment_type_id: form.appointment_type_id || null,
        result: statusIsCompleted ? (form.result || null) : null,
        calendar,
      };
      // Se nessun titolo manuale, usa il nome del cliente come titolo visibile
      if (!payload.title || !payload.title.trim()) {
        if (payload.client_id) {
          const c = clients.find((x) => x.id === payload.client_id);
          if (c?.name) payload.title = c.name;
        }
      }
      if (isNew) {
        await api.post("/appointments", payload);
        toast.success("Appuntamento creato");
      } else {
        await api.patch(`/appointments/${initial.id}`, payload);
        toast.success("Appuntamento aggiornato");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!initial?.id) return;
    if (!window.confirm("Eliminare questo appuntamento?")) return;
    try {
      await api.delete(`/appointments/${initial.id}`);
      toast.success("Appuntamento eliminato");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const canDelete = !isNew && (
    user?.role === "admin" || user?.role === "manager" || user?.role === "receptionist" ||
    initial?.collaborator_id === user?.id
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isNew ? "Nuovo Appuntamento" : "Dettagli Appuntamento"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="apt-title">Titolo <span className="text-muted-foreground font-normal">(facoltativo se cliente selezionato)</span></Label>
            <Input
              id="apt-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Es. Consulenza strategica"
              data-testid="appointment-title-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Consulente</Label>
              <Select
                value={form.collaborator_id}
                onValueChange={(v) => setForm({ ...form, collaborator_id: v })}
                disabled={user?.role === "collaborator"}
              >
                <SelectTrigger data-testid="appointment-collaborator-select">
                  <SelectValue placeholder="Seleziona…" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: u.color }} />
                        {u.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo Appuntamento</Label>
              <Select value={form.appointment_type_id || ""} onValueChange={onTypeChange}>
                <SelectTrigger data-testid="appointment-type-select">
                  <SelectValue placeholder="Nessuno" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                        {t.name} · {t.duration_min}m
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Cliente</Label>
            <ClientPicker
              clients={clients}
              value={form.client_id || ""}
              onChange={(v) => setForm({ ...form, client_id: v })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Inizio</Label>
              <Input
                type="datetime-local"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                data-testid="appointment-start-input"
              />
            </div>
            <div>
              <Label>Fine</Label>
              <Input
                type="datetime-local"
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                data-testid="appointment-end-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase font-bold tracking-widest text-muted-foreground">
              Stato Appuntamento
            </Label>
            <RadioGroup
              value={form.status}
              onValueChange={onStatusChange}
              className="grid gap-2 mt-2"
              style={{ gridTemplateColumns: `repeat(${Math.min(statusList.length, 5)}, minmax(0, 1fr))` }}
            >
              {statusList.map((s) => {
                const meta = metaFor(s);
                const Icon = meta.icon;
                const active = form.status === s;
                return (
                  <label
                    key={s}
                    htmlFor={`st-${s}`}
                    data-testid={`appointment-status-option-${s}`}
                    className={`cursor-pointer rounded-md border px-2 py-2 text-center text-xs font-medium transition-colors ${
                      active ? "text-white" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                    style={active ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
                  >
                    <RadioGroupItem id={`st-${s}`} value={s} className="sr-only" />
                    <span className="inline-flex items-center gap-1.5 justify-center">
                      <Icon className="h-3.5 w-3.5" />
                      {meta.label}
                    </span>
                  </label>
                );
              })}
            </RadioGroup>
          </div>

          {statusIsCompleted && (
            <div
              className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 px-3 py-3"
              data-testid="appointment-result-section"
            >
              <Label className="text-xs uppercase font-bold tracking-widest text-emerald-800 inline-flex items-center gap-1.5">
                <Award className="h-3.5 w-3.5" />
                Esito Appuntamento
              </Label>
              {resultList.length === 0 ? (
                <div className="mt-2 text-xs text-muted-foreground italic">
                  Nessun esito configurato per il calendario {calendar === "trainers" ? "Trainers" : "Consulenti"}. Chiedi all'Admin di aggiungerne.
                </div>
              ) : (
                <RadioGroup
                  value={form.result || ""}
                  onValueChange={(v) => setForm({ ...form, result: v })}
                  className="grid gap-2 mt-2"
                  style={{ gridTemplateColumns: `repeat(${Math.min(resultList.length + 1, 4)}, minmax(0, 1fr))` }}
                >
                  <label
                    htmlFor="res-none"
                    data-testid="appointment-result-option-none"
                    className={`cursor-pointer rounded-md border px-2 py-2 text-center text-xs font-medium transition-colors ${
                      !form.result ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <RadioGroupItem id="res-none" value="" className="sr-only" />
                    <span>Nessuno</span>
                  </label>
                  {resultList.map((r) => {
                    const meta = resultMetaFor(calendar, r);
                    const Icon = meta.icon;
                    const active = form.result === r;
                    return (
                      <label
                        key={r}
                        htmlFor={`res-${r}`}
                        data-testid={`appointment-result-option-${r}`}
                        className={`cursor-pointer rounded-md border px-2 py-2 text-center text-xs font-medium transition-colors ${
                          active ? "text-white" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                        style={active ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
                      >
                        <RadioGroupItem id={`res-${r}`} value={r} className="sr-only" />
                        <span className="inline-flex items-center gap-1.5 justify-center">
                          <Icon className="h-3.5 w-3.5" />
                          {meta.label}
                        </span>
                      </label>
                    );
                  })}
                </RadioGroup>
              )}
            </div>
          )}

          <div>
            <Label>Descrizione</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Argomento dell'incontro…"
              data-testid="appointment-description-textarea"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Note post-incontro</Label>
              {form.post_notes && <Badge variant="secondary">Note inserite</Badge>}
            </div>
            <Textarea
              rows={3}
              value={form.post_notes}
              onChange={(e) => setForm({ ...form, post_notes: e.target.value })}
              placeholder="Riepilogo, action items, prossimi passi…"
              data-testid="appointment-post-notes-textarea"
            />
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between gap-2">
          <div>
            {canDelete && (
              <Button variant="outline" onClick={del} data-testid="delete-appointment-button" className="text-rose-600 hover:text-rose-700">
                <Trash2 className="h-4 w-4 mr-2" /> Elimina
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-appointment-button">
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientPicker({ clients, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = clients.find((c) => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="appointment-client-select"
          className="w-full justify-between font-normal"
        >
          {selected ? (
            <span className="truncate">
              <span className="font-medium">{selected.name}</span>
              {selected.phone ? <span className="text-muted-foreground"> · {selected.phone}</span> : null}
            </span>
          ) : (
            <span className="text-muted-foreground">Nessun cliente — cerca per nome, email o telefono…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(itemValue, search) => {
            if (!search) return 1;
            return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Cerca cliente…" data-testid="appointment-client-search-input" autoFocus />
          <CommandList className="max-h-72">
            <CommandEmpty>Nessun cliente trovato.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="nessun-cliente"
                onSelect={() => { onChange(""); setOpen(false); }}
                data-testid="appointment-client-option-none"
              >
                <UserX className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="text-muted-foreground">Nessun cliente</span>
                {!value && <Check className="ml-auto h-4 w-4" />}
              </CommandItem>
              {clients.map((c) => {
                const haystack = [c.name, c.email, c.phone].filter(Boolean).join(" ");
                return (
                  <CommandItem
                    key={c.id}
                    value={`${haystack} ${c.id}`}
                    onSelect={() => { onChange(c.id); setOpen(false); }}
                    data-testid={`appointment-client-option-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      {(c.email || c.phone) && (
                        <div className="text-[11px] text-muted-foreground truncate">
                          {c.phone}{c.phone && c.email ? " · " : ""}{c.email}
                        </div>
                      )}
                    </div>
                    {value === c.id && <Check className="ml-2 h-4 w-4" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

